import { useEffect, useRef, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eraser } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const PadFirma = ({ onChange, testId }) => {
  const ref = useRef(null);
  const drawing = useRef(false);
  useEffect(() => {
    const c = ref.current;
    const r = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * r;
    c.height = c.offsetHeight * r;
    const ctx = c.getContext("2d");
    ctx.scale(r, r);
    ctx.lineWidth = 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0f172a";
  }, []);
  const pos = (e) => { const b = ref.current.getBoundingClientRect(); return [e.clientX - b.left, e.clientY - b.top]; };
  const down = (e) => { e.preventDefault(); drawing.current = true; const ctx = ref.current.getContext("2d"); ctx.beginPath(); ctx.moveTo(...pos(e)); };
  const move = (e) => { if (!drawing.current) return; const ctx = ref.current.getContext("2d"); ctx.lineTo(...pos(e)); ctx.stroke(); };
  const up = () => { if (drawing.current) { drawing.current = false; onChange(ref.current.toDataURL("image/png")); } };
  const borrar = () => { const c = ref.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); onChange(""); };
  return (
    <div>
      <canvas ref={ref} data-testid={testId} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="h-44 w-full touch-none rounded-md border-2 border-dashed border-slate-300 bg-white" />
      <button type="button" onClick={borrar} data-testid={`${testId}-borrar`} className="mt-1 inline-flex items-center gap-1 text-xs text-slate-500"><Eraser className="h-3.5 w-3.5" />Borrar firma</button>
    </div>
  );
};

const Campo = ({ f, v, set }) => {
  if (f.type === "legal_notice") return (
    <div className="rounded-md bg-slate-50 p-4"><div className="font-semibold">{f.title}</div><p className="mt-2 text-sm leading-relaxed text-slate-700 whitespace-pre-line">{f.body}</p></div>
  );
  if (f.type === "consent_required") return (
    <label className="flex items-start gap-3 rounded-md border-2 border-[#1E3A8A] p-3 text-base font-medium"><input type="checkbox" checked={v === true} onChange={(e) => set(e.target.checked)} data-testid={`fp-${f.id}`} className="mt-1 h-6 w-6" />{f.label} *</label>
  );
  if (f.type === "yesno") return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 py-2">
      <span className="text-sm text-slate-700 flex-1 min-w-[200px]">{f.label}</span>
      {["Sí", "No"].map((o) => <button key={o} type="button" onClick={() => set(o)} data-testid={`fp-${f.id}-${o === "Sí" ? "si" : "no"}`} className={`h-10 w-16 rounded-md border text-sm ${v === o ? "bg-[#1E3A8A] text-white border-[#1E3A8A]" : "bg-white border-slate-300"}`}>{o}</button>)}
    </div>
  );
  return (
    <label className="block text-sm text-slate-600">{f.label}{f.required ? " *" : ""}
      <input value={v || ""} onChange={(e) => set(e.target.value)} data-testid={`fp-${f.id}`} className="mt-1 h-11 w-full rounded-md border border-slate-300 px-3 text-base text-slate-900" />
    </label>
  );
};

export default function FirmaProteccion() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sec, setSec] = useState(null);
  const [p, setP] = useState(null);
  const [r, setR] = useState({});
  const [firma, setFirma] = useState("");
  const [firma2, setFirma2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/proteccion-datos/texto").then((x) => setSec(x.data)).catch((e) => toast.error(errMsg(e)));
    api.get(`/patients/${id}`).then((x) => setP(x.data.patient));
  }, [id]);
  const guardar = async () => {
    if (r.acepta_proteccion_datos !== true) return toast.error("Hay que marcar «He leído y acepto»");
    if (!r.firmante_nombre?.trim() || !r.firmante_dni?.trim()) return toast.error("Falta el nombre o el DNI del firmante");
    if (!firma) return toast.error("Falta la firma");
    if (r.firmante_2_nombre?.trim() && (!r.firmante_2_dni?.trim() || !firma2)) return toast.error("Falta el DNI o la firma del firmante 2");
    setBusy(true);
    try {
      await api.post(`/patients/${id}/consentimientos`, { respuestas: r, firma_png: firma, firma_2_png: firma2 });
      toast.success("Protección de datos firmada y guardada");
      nav(`/pacientes/${id}`);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  if (!sec || !p) return <div className="p-8 text-slate-400">Cargando…</div>;
  return (
    <div className="min-h-screen bg-white pb-24" data-testid="firma-page">
      <div className="mx-auto max-w-3xl space-y-4 p-6">
        <Link to={`/pacientes/${id}`} className="inline-flex items-center gap-1 text-sm text-slate-500" data-testid="firma-volver"><ArrowLeft className="h-4 w-4" />Volver a la ficha</Link>
        <div className="flex items-center gap-4"><img src="/logo-sv.jpg" alt="Salud y Visión" className="h-14" />
          <div><h1 className="font-[Outfit] text-2xl font-bold">{sec.title}</h1><div className="text-sm text-slate-500">Paciente: {p.nombre}</div></div></div>
        {sec.fields.map((f) => <Campo key={f.id} f={f} v={r[f.id]} set={(v) => setR((x) => ({ ...x, [f.id]: v }))} />)}
        <div><div className="mb-1 text-sm font-semibold">Firma del firmante 1 *</div><PadFirma onChange={setFirma} testId="fp-firma-1" /></div>
        {r.firmante_2_nombre?.trim() && <div><div className="mb-1 text-sm font-semibold">Firma del firmante 2</div><PadFirma onChange={setFirma2} testId="fp-firma-2" /></div>}
        <button onClick={guardar} disabled={busy} data-testid="fp-guardar" className="h-14 w-full rounded-md bg-[#1E3A8A] text-lg font-semibold text-white disabled:opacity-60">{busy ? "Guardando…" : "Firmar y guardar"}</button>
      </div>
    </div>
  );
}
